import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Figma } from "lucide-react";
import { projects } from "../../data/Projects";
import { General } from "../../data/General";

const Projects: React.FC = () => {
  return (
    <div className="min-h-screen text-white bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Navigation */}
      <nav className="fixed top-0 z-50 w-full border-b bg-slate-900/80 backdrop-blur-sm border-slate-700">
        <div className="flex items-center justify-between max-w-6xl px-6 py-4 mx-auto">
          <Link
            to="/"
            className="text-xl font-bold text-transparent bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text"
          >
            {General.name}
          </Link>
          <div className="hidden gap-8 md:flex">
            <Link
              to="/"
              className="transition-colors text-slate-300 hover:text-white"
            >
              Home
            </Link>
            <Link
              to="/projects"
              className="transition-colors text-slate-300 hover:text-white"
            >
              Projects
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-20">
        {/* Projects Header */}
        <section className="max-w-4xl px-6 py-20 mx-auto text-center">
          <h1 className="mb-6 text-4xl font-bold md:text-6xl">
            My{" "}
            <span className="text-transparent bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text">
              Projects
            </span>
          </h1>
          <p className="max-w-2xl mx-auto mb-8 text-xl text-slate-300">
            A collection of design projects that showcase my approach to
            problem-solving and visual design
          </p>
        </section>

        {/* Projects Grid Section */}
        <section className="py-10 bg-slate-800/30">
          <div className="max-w-6xl px-6 mx-auto">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {projects.map((project) => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="overflow-hidden transition-all duration-300 border bg-slate-800/50 rounded-xl backdrop-blur-sm border-slate-700 hover:border-cyan-500/50 group hover:transform hover:scale-105"
                >
                  {/* Project Image */}
                  <div className="relative overflow-hidden aspect-video bg-gradient-to-br from-slate-700 to-slate-800">
                    <div className="absolute inset-0 flex items-center justify-center text-slate-400">
                       <img  src={project.images[0]} className="object-cover w-96"/>
                    </div>

                    {/* Hover Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center transition-opacity duration-300 opacity-0 bg-cyan-500/10 group-hover:opacity-100">
                      <div className="font-semibold text-cyan-400">
                        View Case Study →
                      </div>
                    </div>
                  </div>

                  {/* Project Info */}
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <span className="text-sm font-medium text-cyan-400">
                          {project.category}
                        </span>
                        <h3 className="mt-1 text-lg font-semibold transition-colors group-hover:text-cyan-400">
                          {project.title}
                        </h3>
                      </div>
                      <ExternalLink className="w-5 h-5 transition-colors text-slate-400 group-hover:text-cyan-400" />
                    </div>

                    <p className="mb-4 text-sm text-slate-300 line-clamp-2">
                      {project.desc}
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {project.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 text-xs border rounded-full bg-slate-700/50 text-slate-300 border-slate-600"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Back to Home Button */}
            <div className="mt-12 text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 transition-colors border rounded-lg border-slate-600 hover:border-cyan-500 hover:text-cyan-400"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Projects;
